const StoreStatusUtils = require('header.storeStatusUtils');

const storeStatus = {
  CLOSED_NORMAL: 'closed normal',
  CLOSED_DISASTER: 'closed disaster',
  CLOSED_HOLIDAY: 'closed holiday',
  OPEN: 'open',
};

const OPEN = 'OPEN';
const CLOSED = 'CLOSED';
const TEMPORARILY_CLOSED = 'TEMPORARILY CLOSED';

const classNames = {
  CLOSED: 'closed',
  OPEN: 'open',
};

const colors = {
  OPEN: '#00873C',
  CLOSED: '#D81118',
};

const abbreviatedDays = {
  sunday: 'Sun',
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
};

function getDateCallback() {
  return new Date();
}

/*
  Takes a timestamp in the format of 'hh:mm' and reformats
  it to 'h am' or 'h pm'.
*/
function formatTimestamp(timestamp) {
  if (!timestamp) return null;

  const hour = Number(timestamp.split(':')[0]);

  if (hour === 0) {
    return '12 am';
  } else if (hour === 12) {
    return '12 pm';
  } else if (hour > 12) {
    return `${hour - 12} pm`;
  }

  return `${hour} am`;
}

/*
  UX wants us to do more than just distinguish between basic open and closed
  states for stores.

  Specifically, they want us to have three distinct classifications for closed:
    1.  Normal, encompassed by the following:
        A. Was open today, opens tomorrow.
        B. Opens today.

    2.  Disaster: store is closed for at least the next 6 days, due to a natural disaster
        or other unforeseen circumstances.

    3.  Holiday: store is closed all day either today or tomorrow.
*/
function getStoreOpenClosedStatus(storeStatusUtils) {
  if (storeStatusUtils.isOpenNow()) {
    return storeStatus.OPEN;
  }

  /*
    If we have no opening times for at least the next 6 days,
    then we fall into the natural disaster / unforeseen circumstances scenario.
  */
  if (storeStatusUtils.isClosedRestOfWeek()) {
    return storeStatus.CLOSED_DISASTER;
  }

  /*
    When a store is currently closed, if it's closed for the entirety of either
    the current or next day it qualifies as a holiday.
  */
  if (storeStatusUtils.isClosedToday() || storeStatusUtils.isClosedTomorrow()) {
    return storeStatus.CLOSED_HOLIDAY;
  }

  /*
    The store neither was closed today nor is it closed tomorrow.
    Therefore, it cannot be a holiday.
  */
  return storeStatus.CLOSED_NORMAL;
}

/*
    This method returns the strings we use to build the store hours messaging
    in the main header (here called primary) section.

    For a store that's open, you'll only ever see the following:
    'OPEN until X' where X is the close time for that day.

    The messaging for a store that's currently closed is more complicated.
    We have three different scenarios:
      1.  Normal day.

          The store was open today and will open tomorrow.

          Example output: {
            openClosedClass: 'closed',
            storeStatus: 'CLOSED',
            storeHoursElaboration: 'until 6 am',
          }

      2.  Disaster.

          Due to unforeseen circumstances like a natural disaster,
          the store is closed for at least the next 6 days.

          Example output: {
            openClosedClass: 'closed',
            storeStatus: 'TEMPORARILY CLOSED',
            storeHoursElaboration: null,
          }

      3.  Holiday.

          The store was either (1) closed all day today or (2) is closed all day tomorrow
          (but not for the rest of the week).

          You would also see this messaging if you were coming to the end of a
          Disaster scenario. (Maybe your store was closed for the remainder of
          the previous week, but will reopen sometime this week.)

          Example output: {
            openClosedClass: 'closed',
            storeStatus: 'CLOSED',
            storeHoursElaboration: '| Opens Mon 6 am',
          }
  */
function getPrimaryStoreHoursMessaging(storeHours) {
  const storeStatusUtils = new StoreStatusUtils(storeHours, getDateCallback);

  const storeOpenCloseStatus = getStoreOpenClosedStatus(storeStatusUtils);

  switch (storeOpenCloseStatus) {
    case storeStatus.OPEN: {
      const closingTime = formatTimestamp(storeStatusUtils.getCloseTime());
      return {
        openClosedClass: classNames.OPEN,
        storeStatusMessage: OPEN,
        storeHoursElaboration: closingTime ? `until ${closingTime}` : null,
      };
    }
    case storeStatus.CLOSED_DISASTER: {
      return {
        openClosedClass: classNames.CLOSED,
        storeStatusMessage: TEMPORARILY_CLOSED,
        storeHoursElaboration: null,
      };
    }
    case storeStatus.CLOSED_HOLIDAY: {
      const { day, time } = storeStatusUtils.getNextOpenDayTime();
      const nextOpenDay = abbreviatedDays[day];
      const nextOpenTime = formatTimestamp(time);
      return {
        openClosedClass: classNames.CLOSED,
        storeStatusMessage: CLOSED,
        storeHoursElaboration:
          nextOpenDay && nextOpenTime
            ? `| Opens ${nextOpenDay} ${nextOpenTime}`
            : null,
      };
    }
    default: {
      // handles the CLOSED_NORMAL case
      const openingTime = formatTimestamp(
        storeStatusUtils.getNextOpenDayTime().time,
      );
      return {
        openClosedClass: classNames.CLOSED,
        storeStatusMessage: CLOSED,
        storeHoursElaboration: openingTime ? `until ${openingTime}` : null,
      };
    }
  }
}

module.exports = {
  getPrimaryStoreHoursMessaging,
};
